import { useState, useRef, useEffect } from "react"
import { Download, Sparkles, NotebookPen, Check,ExternalLink  } from "lucide-react"
import html2canvas from "html2canvas"
import JSZip from "jszip"
import { postAws } from "../postAws"

const Flashcard = ({ front, back, book }) => {
  const [isFlipped, setIsFlipped] = useState(false)
  const [cardHeight, setCardHeight] = useState()
  const [isHovered, setIsHovered] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const flashcardRef = useRef(null)
  const frontRef = useRef(null)
  const backRef = useRef(null)

  // First useEffect: Calculate initial height
  useEffect(() => {
    const updateCardHeight = () => {
      if (frontRef.current && backRef.current) {
        const frontHeight = frontRef.current.scrollHeight
        const backHeight = backRef.current.scrollHeight
        setCardHeight(Math.max(frontHeight, backHeight ) + 40)
      }
    }

    updateCardHeight()
    window.addEventListener("resize", updateCardHeight)
    return () => window.removeEventListener("resize", updateCardHeight)
  }, [])

  // Second useEffect: Force reflow after mount
  useEffect(() => {
    setTimeout(() => {
      setCardHeight((prev) => prev + 1) // Small update to trigger reflow
    }, 100)
  }, [])

  const toggleFlip = () => {
    setIsFlipped(!isFlipped)
  }

  const downloadFlashcardImages = async () => {
    if (!flashcardRef.current) return

    // Create temporary containers for capturing
    const createTempContainer = (content, isBack = false) => {
      const container = document.createElement("div")
      container.style.width = "800px"
      container.style.height = `${cardHeight}px`
      container.style.position = "absolute"
      container.style.left = "-9999px"
      container.style.padding = "24px"
      container.style.borderRadius = "16px"
      container.style.display = "flex"
      container.style.alignItems = "center"
      container.style.justifyContent = "center"

      if (isBack) {
        container.style.background = "linear-gradient(to bottom right, rgb(99, 102, 241), rgb(79, 70, 229))"
        container.style.color = "white"
      } else {
        container.style.background = "linear-gradient(to bottom right, white, rgb(243, 244, 246))"
        container.style.border = "1px solid rgb(229, 231, 235)"
        container.style.color = "rgb(31, 41, 55)"
      }

      const contentDiv = document.createElement("div")
      contentDiv.style.width = "100%"
      contentDiv.style.padding = "32px"
      contentDiv.innerHTML = content
      container.appendChild(contentDiv)

      document.body.appendChild(container)
      return container
    }

    // Capture front
    const frontContent = `<div style="text-align: center; font-size: 1.25rem;">${front}</div>`
    const tempFront = createTempContainer(frontContent)
    const frontCanvas = await html2canvas(tempFront, {
      scale: 2,
      logging: false,
      useCORS: true,
      allowTaint: true,
    })
    document.body.removeChild(tempFront)

    // Capture back
    const backContent = `<ul style="list-style-type: disc; padding-left: 1.5rem; text-align: left;">
      ${Array.isArray(back) ? back.map((point) => `<li style="margin-bottom: 0.75rem;">${point}</li>`).join("") : `<li>${back}</li>`}
    </ul>`
    const tempBack = createTempContainer(backContent, true)
    const backCanvas = await html2canvas(tempBack, {
      scale: 2,
      logging: false,
      useCORS: true,
      allowTaint: true,
    })
    document.body.removeChild(tempBack)

    // Create and download ZIP
    const zip = new JSZip()
    zip.file("flashcard_front.png", frontCanvas.toDataURL("image/png").split("base64,")[1], { base64: true })
    zip.file("flashcard_back.png", backCanvas.toDataURL("image/png").split("base64,")[1], { base64: true })

    const content = await zip.generateAsync({ type: "blob" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(content)
    link.download = "flashcard_images.zip"
    link.click()
  }

  const handleSaveToNotes = async () => {
    const notesData = {
      classRoomId: localStorage.getItem("classRoomId"),
      noteType: "flashCard",
      noteTitle: `${book.fileTitle} Flashcard`,
      refTitle: book.fileTitle,
      refId: book._id,
      userId: localStorage.getItem("teacherId"),
      noteContent: JSON.stringify({
        front,
        back,
      }),
      mediaUrl: "",
    }

    const saveNote = await postAws("https://api-dev.aurait.ai/notes", "POST", notesData)
    if (saveNote.statusCode === 201) {
      setIsSaved(true)
    }
  }

  const truncateText = (text, maxWords = 30) => {
    const words = text.split(" ")
    if (words.length > maxWords) {
      return words.slice(0, maxWords).join(" ") + "..."
    }
    return text
  }

  return (
    <div className="bg-gradient-to-br from-white to-gray-100 border border-white/50 shadow-lg rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl w-[280px] mx-auto">
      <div className="flex items-center justify-between mb-4 w-full">
        <div className="flex items-center gap-2">
          <Sparkles className="text-green-500 animate-bounce" />
          <h3 className="text-lg font-semibold text-indigo-600">Flashcard</h3>
        </div>
        <div className="flex items-center gap-4 -mt-2">
        {book && (
  <div className="relative flex items-center gap-3">
    {/* Save Button */}


    {/* Tooltip for Save Button */}
    {isHovered && !isSaved && (
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-md whitespace-nowrap shadow-lg">
        Save to Notes
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
      </div>
    )}

    {/* Notes Redirect Icon (Appears After Saving) */}
    {isSaved && (
      <button
        onClick={() => window.open("/notes", "_blank")}
        className="p-2 bg-purple-500 hover:bg-purple-600 text-white rounded-full transition-all duration-300 ease-in-out shadow-lg"
        aria-label="Go to Notes"
      >
         <ExternalLink size={18} />
      </button>
    )}
  </div>
)}

          <button
            onClick={downloadFlashcardImages}
            className="flex items-center gap-1 px-2 py-1 text-sm bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 mr-5"
          >
            <Download size={14} />
          </button>
        </div>
      </div>

      <div className="text-center text-sm text-gray-500 mb-4">Click the card to flip</div>

      <div className="flex items-center justify-center">
        <div
          ref={flashcardRef}
          className="relative w-full cursor-pointer"
          style={{ height: `${cardHeight}px` }}
          onClick={toggleFlip}
        >
          <div
            className={`relative w-full h-full transition-transform duration-700 [transform-style:preserve-3d] ${
              isFlipped ? "[transform:rotateY(180deg)]" : ""
            }`}
          >
            <div
              ref={frontRef}
              className="flashcard-face front absolute w-full h-full [backface-visibility:hidden] flex items-center justify-center rounded-2xl bg-gradient-to-br from-white to-gray-100 border border-gray-200 text-gray-800 shadow-md"
            >
              <div className="text-center w-full flex items-center justify-center p-8">
                <p className="text-xl font-medium text-gray-800 whitespace-normal max-w-[90%] leading-tight">{front}</p>
              </div>
            </div>
            <div
              ref={backRef}
              className="flashcard-face back absolute w-full [backface-visibility:hidden] flex items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-600 text-white shadow-md [transform:rotateY(180deg)]"
            >
              <div className="w-full p-8">
                <ul className="list-disc text-left pl-6 space-y-3">
                  {Array.isArray(back) ? (
                    back.map((point, index) => (
                      <li key={index} className="text-sm leading-relaxed">
                        {truncateText(point)}
                      </li>
                    ))
                  ) : (
                    <li className="text-sm leading-relaxed">{truncateText(back)}</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Flashcard



